'use client'

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utilities/ui'
import {
  Check,
  CreditCard,
  Shield,
  Zap,
  X,
  Star,
  ArrowRight,
  Sparkles,
  Loader2,
} from 'lucide-react'
import { redirect, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { BrandWordmark } from '@/components/ui/BrandWordmark'
import Image from 'next/image'

// Subscription plans data
export const subscriptionPlans = [
  // {
  //   title: 'Basic',
  //   description: 'Basic free plan',
  // },
  {
    title: 'D.R.I.V.E. Mastery',
    description: 'Newer sellers building a strong foundation',
    // One-time purchase: use the `annual` entry to carry the one-time price
    productType: 'course',
    annual: {
      title: 'Drive Mastery (One-Time)',
      price: 49000,
      plan_id: 'drive-mastery',
    },
    features: [
      'D.R.I.V.E. Success Mastery course',
      'Access to Drive AI Pro',
      'Unlimited assessment invites for 12 months',
      'Downloadable D.R.I.V.E. PDFs',
      'Weekly DRIVE Coaching',
    ],
  },
  // {
  //   title: 'Financial Mastery',
  //   description: 'Sales-driven entrepreneurs focused on financial growth',
  //   monthly: {
  //     title: 'Financial Mastery',
  //     price: 12900,
  //     plan_id: 'financial-mastery-plan-monthly',
  //   },
  //   annual: {
  //     title: 'Financial Mastery (Annual)',
  //     price: 129000,
  //     plan_id: 'financial-mastery-plan-annual',
  //   },
  //   features: [
  //     'D.R.I.V.E. Success Mastery and Financial Mastery courses',
  //     'Access to Drive AI Pro',
  //     'Up to 10 D.R.I.V.E. Assessments per month',
  //     'Downloadable D.R.I.V.E. PDFs',
  //     'Weekly Mastermind Mondays',
  //     'Ongoing content updates',
  //   ],
  // },
  // {
  //   title: 'Digital Business Mastery',
  //   description: 'Complete business transformation for established entrepreneurs',
  //   monthly: {
  //     title: 'Digital Business Mastery',
  //     price: 14900,
  //     plan_id: 'digital-business-mastery-plan-monthly',
  //   },
  //   annual: {
  //     title: 'Digital Business Mastery (Annual)',
  //     price: 149000,
  //     plan_id: 'digital-business-mastery-plan-annual',
  //   },
  //   popular: true,
  //   badge: 'Most Popular',
  //   features: [
  //     'D.R.I.V.E. Success Mastery, Financial Mastery, and Digital Business Mastery courses',
  //     'Access to Drive AI Pro',
  //     'Unlimited D.R.I.V.E. Assessments',
  //     'Downloadable D.R.I.V.E. PDFs',
  //     'Weekly Mastermind Mondays',
  //     'Ongoing content updates',
  //   ],
  // },
]

// Collect.js configuration type
declare global {
  interface Window {
    CollectJS: {
      configure: (config: unknown) => void
    }
    openPurchaseModal?: (opts?: {
      productType?: 'plan' | 'course' | 'module'
      productId?: string
      productTitle?: string
      amount?: number
      gift?: boolean
    }) => void
  }
}

// Simplified hosted checkout modal component with Collect.js
function HostedCheckoutModal({
  isOpen,
  onClose,
  selectedPlan,
  billingCycle,
  serializedUser,
  productOverride,
  isGift,
}: {
  isOpen: boolean
  onClose: () => void
  selectedPlan: (typeof subscriptionPlans)[0] | null
  billingCycle: 'annual'
  serializedUser: {
    name: string
    email: string
  } | null
  productOverride?: {
    productType: 'plan' | 'course' | 'module'
    productId: string
    productTitle: string
    amount: number
  } | null
  isGift: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [collectJsLoaded, setCollectJsLoaded] = useState(false)
  const initializedRef = useRef(false)
  const configuredRef = useRef(false)
  const [instanceKey, setInstanceKey] = useState(0)

  // Load Collect.js script
  useEffect(() => {
    if (isOpen && !collectJsLoaded && !initializedRef.current) {
      const tokenizationKey = process.env.NEXT_PUBLIC_NMI_TOKENIZATION_KEY

      if (!tokenizationKey) {
        console.error('NEXT_PUBLIC_NMI_TOKENIZATION_KEY is not set')
        setMessage({
          type: 'error',
          text: 'Payment system configuration error. Please contact support.',
        })
        return
      }

      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src="https://secure.nmi.com/token/Collect.js"]',
      )
      if (existingScript) {
        setCollectJsLoaded(true)
        initializedRef.current = true
        return
      }

      const script = document.createElement('script')
      script.src = 'https://secure.nmi.com/token/Collect.js'
      script.setAttribute('data-tokenization-key', tokenizationKey)
      script.setAttribute('data-variant', 'inline')
      script.onload = () => {
        setCollectJsLoaded(true)
      }
      script.onerror = () => {
        console.error('Failed to load Collect.js script')
        setMessage({ type: 'error', text: 'Failed to load payment system. Please try again.' })
      }
      document.head.appendChild(script)
      initializedRef.current = true

      return () => {
        // Don't remove the script on cleanup to prevent re-registration issues
        // The script will be reused for subsequent modal opens
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, collectJsLoaded])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessage(null)
      setLoading(false)
      // Don't reset collectJsLoaded to prevent re-initialization
      configuredRef.current = false
    } else {
      // Bump instance key so new DOM IDs are used for CollectJS binding
      setInstanceKey((k) => (k + 1) % 1_000_000)
    }
  }, [isOpen])

  const initializeCollectJS = () => {
    if (configuredRef.current) return
    if (typeof window !== 'undefined' && window.CollectJS) {
      try {
        const priceCents = (productOverride?.amount ?? selectedPlan?.annual?.price) || 0
        if (!priceCents || priceCents <= 0) {
          // Wait until we have a valid price before configuring
          return
        }
        window.CollectJS.configure({
          paymentSelector: `#subscribeButton-${instanceKey}`,
          variant: 'inline',
          invalidCss: {
            color: '#ef4444',
            'border-color': '#ef4444',
          },
          validCss: {
            color: '#ffffff',
            'border-color': '#22c55e',
          },
          placeholderCss: {
            color: '#9ca3af',
            'background-color': 'transparent',
          },
          focusCss: {
            color: '#ffffff',
            'border-color': '#3b82f6',
          },
          fields: {
            ccnumber: {
              selector: `#ccnumber-${instanceKey}`,
              title: 'Card Number',
              placeholder: '0000 0000 0000 0000',
            },
            ccexp: {
              selector: `#ccexp-${instanceKey}`,
              title: 'Card Expiration',
              placeholder: '00 / 00',
            },
            cvv: {
              display: 'show',
              selector: `#cvv-${instanceKey}`,
              title: 'CVV Code',
              placeholder: '123',
            },
          },
          validationCallback: function (field: string, status: boolean, message: string) {
            if (!status) {
              setMessage({ type: 'error', text: `${field}: ${message}` })
            }
          },
          timeoutDuration: 10000,
          timeoutCallback: function () {
            setMessage({ type: 'error', text: 'Payment form timeout. Please try again.' })
          },
          fieldsAvailableCallback: function () {
            // Fields loaded successfully
          },
          callback: function (response: { token: string; error: string }) {
            if (response.token) {
              // Add the payment token to the form (following NMI example)
              const form = document.getElementById('paymentForm') as HTMLFormElement
              const tokenInput = document.createElement('input')
              tokenInput.type = 'hidden'
              tokenInput.name = 'payment_token'
              tokenInput.value = response.token
              form.appendChild(tokenInput)

              // Submit the form data to our API
              handleFormSubmission(response.token)
            } else {
              if (response.error) {
                setMessage({
                  type: 'error',
                  text: `Payment error: ${response.error}`,
                })
              } else {
                setMessage({
                  type: 'error',
                  text: 'Payment token generation failed. Please check your card details and try again.',
                })
              }
            }
          },
        })
        configuredRef.current = true
      } catch (error) {
        console.error('Error configuring Collect.js:', error)
        setMessage({ type: 'error', text: 'Payment form configuration failed. Please try again.' })
      }
    } else {
      console.error('CollectJS not available on window object')
    }
  }

  // Reconfigure Collect.js whenever the modal opens or the plan/cycle changes
  // This ensures the correct price and callbacks are bound for each of the 6 buttons
  useEffect(() => {
    const priceCents = (productOverride?.amount ?? selectedPlan?.annual?.price) || 0
    if (isOpen && collectJsLoaded && !configuredRef.current && priceCents > 0) {
      initializeCollectJS()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, collectJsLoaded, selectedPlan, productOverride, instanceKey])

  const handleFormSubmission = async (paymentToken: string) => {
    setLoading(true)
    setMessage(null)

    // Get form data
    const form = document.getElementById('paymentForm') as HTMLFormElement
    const formData = new FormData(form)

    const billingName = formData.get('billingName') as string
    const billingEmail = formData.get('billingEmail') as string
    const billingZip = formData.get('billingZip') as string
    const recipientName = formData.get('recipientName') as string
    const recipientEmail = formData.get('recipientEmail') as string

    try {
      const response = await fetch('/api/nmi/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productType: productOverride?.productType || (selectedPlan as any)?.productType || 'plan',
          productId: productOverride?.productId || selectedPlan?.annual?.plan_id,
          productTitle: productOverride?.productTitle || selectedPlan?.title,
          amount: productOverride?.amount ?? selectedPlan?.annual?.price,
          billingName,
          billingZip,
          billingEmail,
          paymentToken,
          recipientName: isGift ? recipientName : undefined,
          recipientEmail: isGift ? recipientEmail : undefined,
        }),
      })

      const result = await response.json()

      console.log('API response:', result) // Debug log

      if (result.success) {
        setMessage({
          type: 'success',
          text:
            result.message ||
            (isGift ? 'Gift purchase completed!' : 'Purchase completed successfully!'),
        })

        // Delay redirect to allow user to see success message
        setTimeout(() => {
          onClose()
          setMessage(null)
          redirect('/dashboard')
        }, 3000)
      } else {
        setMessage({ type: 'error', text: result.message || 'Payment failed. Please try again.' })
      }
    } catch (error) {
      console.error('Payment processing error:', error)
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !selectedPlan) return null
  if (typeof document === 'undefined' || !document.body) return null

  const formattedPrice = `$${(selectedPlan?.annual?.price || 0) / 100}`

  return createPortal(
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-card text-card-foreground rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-border animate-scale-in">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Complete Your Purchase</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Plan Summary */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6 border border-border">
            <h3 className="font-semibold text-lg">{selectedPlan.title}</h3>
            <p className="text-muted-foreground text-sm mb-2">{selectedPlan.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold">{formattedPrice}</span>
              <span className="text-sm text-muted-foreground">One-time</span>
            </div>
          </div>

          {/* Simplified Payment Form */}
          <form id="paymentForm" className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <input
                name="billingName"
                type="text"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors border-border"
                placeholder="Enter your full name"
                defaultValue={serializedUser?.name || ''}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                name="billingEmail"
                type="email"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors border-border"
                placeholder="Enter your email address"
                defaultValue={serializedUser?.email || ''}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">ZIP Code</label>
              <input
                name="billingZip"
                type="text"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors border-border"
                placeholder="Enter your ZIP code"
                required
              />
            </div>

            {/* Collect.js Credit Card Fields */}
            <div>
              <label className="block text-sm font-medium mb-2">Card Number</label>
              <div
                id={`ccnumber-${instanceKey}`}
                className="w-full px-3 py-2 border rounded-lg border-border bg-background min-h-[40px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Expiry Date</label>
                <div
                  id={`ccexp-${instanceKey}`}
                  className="w-full px-3 py-2 border rounded-lg border-border bg-background min-h-[40px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">CVV</label>
                <div
                  id={`cvv-${instanceKey}`}
                  className="w-full px-3 py-2 border rounded-lg border-border bg-background min-h-[40px]"
                />
              </div>
            </div>

            {/* Security Notice */}
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Shield size={16} className="mt-0.5 flex-shrink-0" />
              <p>
                Your payment information is secure and encrypted. We use industry-standard SSL
                encryption and never store your card details.
              </p>
            </div>

            {/* Gift Recipient Fields */}
            {isGift && (
              <div className="rounded-lg p-3 border border-border bg-muted/20">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Recipient Full Name</label>
                    <input
                      name="recipientName"
                      type="text"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors border-border"
                      placeholder="Who are you gifting this to?"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Recipient Email</label>
                    <input
                      name="recipientEmail"
                      type="email"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background transition-colors border-border"
                      placeholder="recipient@email.com"
                      required
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  We will send your recipient an email with login instructions and a temporary
                  password.
                </p>
              </div>
            )}

            {/* Account Creation Notice */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <p className="text-sm text-primary">
                <strong>Note:</strong> If you don&apos;t have an account yet, one will be created
                automatically with your email address
                {isGift ? ', and your recipient will receive their own account details' : ''}.
                You&apos;ll receive {isGift ? 'a confirmation' : 'login credentials'} via email
                after successful payment.
              </p>
            </div>

            {/* Message Display */}
            {message && (
              <div
                className={cn(
                  'p-3 rounded-lg text-sm',
                  message.type === 'success'
                    ? 'bg-success/20 border border-success/20'
                    : 'bg-destructive/20 text-destructive border border-destructive/20',
                )}
              >
                {message.text}
              </div>
            )}

            <Button
              id={`subscribeButton-${instanceKey}`}
              variant="default"
              type="button"
              disabled={loading || !collectJsLoaded}
              className="w-full flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              onClick={() => {
                const form = document.getElementById('paymentForm') as HTMLFormElement
                if (!form) return

                const formData = new FormData(form)
                const requiredFields = [
                  'billingName',
                  'billingEmail',
                  'billingZip',
                  ...(isGift ? ['recipientName', 'recipientEmail'] : []),
                ]
                const missingFields = requiredFields.filter((field) => !formData.get(field))

                if (missingFields.length > 0) {
                  setMessage({
                    type: 'error',
                    text: 'Please fill in all required fields before proceeding with payment.',
                  })
                  return
                }

                setMessage(null)
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Processing Payment...
                </>
              ) : !collectJsLoaded ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Loading Payment Form...
                </>
              ) : (
                <>
                  <CreditCard size={16} />
                  Buy Now - {formattedPrice}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface SubscriptionPlansProps {
  showBillingToggle?: boolean
  showAdditionalInfo?: boolean
  className?: string
  currentUser?: {
    id: string
    name: string
    email: string
    subscriptionLevel: string
    username: string
    updatedAt?: string
    createdAt?: string
  }
  coursePurchase?: {
    title: string
    slug: string
    nmiProductId?: string
    priceCents?: number
  }
  autoOpen?: boolean
}

export default function SubscriptionPlans({
  showBillingToggle = false,
  showAdditionalInfo = true,
  className = '',
  currentUser,
  coursePurchase,
  autoOpen = false,
}: SubscriptionPlansProps) {
  // Force one-time (annual) pricing flow
  const billingCycle: 'annual' = 'annual'
  const [selectedPlan, setSelectedPlan] = useState<(typeof subscriptionPlans)[0] | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [productOverride, setProductOverride] = useState<{
    productType: 'plan' | 'course' | 'module'
    productId: string
    productTitle: string
    amount: number
  } | null>(null)
  const [isGift, setIsGift] = useState(false)
  const searchParams = useSearchParams()

  const handleSubscribe = (plan: (typeof subscriptionPlans)[0]) => {
    // console.log('handleSubscribe', plan)
    setSelectedPlan(plan)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    // Keep selected plan and product override so re-open is instantaneous
  }

  const formatPrice = (amount: number) => `$${(amount / 100).toFixed(0)}`

  const serializedUser = currentUser
    ? {
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        subscriptionLevel: currentUser.subscriptionLevel,
        username: currentUser.username,
      }
    : null

  //console.log('serializedUser', serializedUser)

  // Auto-open modal for purchases via URL params; react to changes without refresh
  useEffect(() => {
    if (!searchParams) return
    const buyParam = String(searchParams.get('buy') || '').toLowerCase()
    const giftParam = String(searchParams.get('gift') || '').toLowerCase()
    const productType = searchParams.get('productType') as 'plan' | 'course' | 'module' | null
    const productId = searchParams.get('productId') || undefined
    const productTitle = searchParams.get('productTitle') || undefined
    const amountStr = searchParams.get('amount') || undefined
    const amount = amountStr ? parseInt(String(amountStr), 10) : NaN

    // Gift mode toggle
    setIsGift(giftParam === '1' || giftParam === 'true' || giftParam === 'yes')

    // If product details passed in query, configure override and selected plan
    if (productType && productId && productTitle && Number.isFinite(amount)) {
      setProductOverride({ productType, productId, productTitle, amount })
      setSelectedPlan({
        title: productTitle,
        description: productType === 'module' ? 'Module purchase' : 'Course purchase',
        annual: { title: productTitle, price: amount, plan_id: productId },
        features: [],
      } as any)
    } else if (coursePurchase) {
      // Fall back to server-provided course details when none in query and no plan selected yet
      const cp = coursePurchase
      const pCents = cp.priceCents ?? 0
      const pid = cp.nmiProductId || cp.slug
      setSelectedPlan({
        title: cp.title,
        description: 'Course purchase',
        productType: 'course' as const,
        annual: { title: cp.title, price: pCents, plan_id: pid },
        features: [],
      } as any)
      setProductOverride({
        productType: 'course',
        productId: pid,
        productTitle: cp.title,
        amount: pCents,
      })
    }

    // If buy flag is present, open the modal
    if (buyParam === '1' || buyParam === 'true') {
      // ensure we have a selected plan
      if (!selectedPlan) {
        const title =
          productTitle ||
          productOverride?.productTitle ||
          (coursePurchase?.title ?? 'Course purchase')
        const price = Number.isFinite(amount)
          ? amount
          : (productOverride?.amount ?? coursePurchase?.priceCents ?? 0)
        const planId =
          productId ||
          productOverride?.productId ||
          coursePurchase?.nmiProductId ||
          coursePurchase?.slug ||
          'drive-mastery'
        setSelectedPlan({
          title,
          description: 'Course purchase',
          annual: { title, price, plan_id: planId },
          features: [],
        } as any)
      }
      setIsModalOpen(true)
    }
    // Re-run when the query string object changes
  }, [searchParams])

  // Expose a global opener so CTAs on /pricing can re-open the modal repeatedly
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.openPurchaseModal = (opts) => {
      // Optional gift toggle
      setIsGift(Boolean(opts?.gift))

      // If explicit product provided, use it
      if (
        opts?.productType &&
        opts?.productId &&
        opts?.productTitle &&
        Number.isFinite(opts?.amount as number)
      ) {
        const amount = Number(opts.amount)
        setProductOverride({
          productType: opts.productType,
          productId: opts.productId,
          productTitle: opts.productTitle,
          amount,
        })
        setSelectedPlan({
          title: opts.productTitle,
          description: opts.productType === 'module' ? 'Module purchase' : 'Course purchase',
          annual: { title: opts.productTitle, price: amount, plan_id: opts.productId },
          features: [],
        } as any)
      } else if (coursePurchase) {
        // Default to course provided by server
        const title = coursePurchase.title
        const amount = coursePurchase.priceCents ?? 0
        const pid = coursePurchase.nmiProductId || coursePurchase.slug
        setProductOverride({ productType: 'course', productId: pid, productTitle: title, amount })
        setSelectedPlan({
          title,
          description: 'Course purchase',
          annual: { title, price: amount, plan_id: pid },
          features: [],
        } as any)
      } else if (!selectedPlan) {
        // Fallback to first plan if available
        setSelectedPlan(subscriptionPlans[0] || null)
      }

      setIsModalOpen(true)
    }
    return () => {
      delete window.openPurchaseModal
    }
  }, [coursePurchase, selectedPlan])

  return (
    <div className={className}>
      {/* Billing toggle hidden for one-time purchase flow */}

      {/* Plans Grid */}
      <div
        className={cn(
          'grid gap-8 items-center max-w-7xl mx-auto',
          subscriptionPlans.length === 1
            ? 'grid-cols-1 place-items-center'
            : 'md:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {subscriptionPlans.map((plan) => (
          <div
            key={plan.title}
            className={cn(
              'relative flex flex-col bg-card rounded-2xl border p-8 transition-all duration-300 hover:shadow-2xl group',
              'popular' in plan && (plan as any).popular
                ? 'border-primary shadow-xl scale-105'
                : 'border-border hover:border-primary/50 shadow-lg',
            )}
          >
            {'badge' in plan && (plan as any).badge && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span
                  className={cn(
                    'px-4 py-1 rounded text-sm font-medium shadow-lg',
                    (plan as any).badge === 'Most Popular'
                      ? 'bg-neutral-800 text-white'
                      : 'bg-yellow-500',
                  )}
                >
                  {(plan as any).badge === 'Most Popular' && (
                    <Star size={12} className="inline mr-1" />
                  )}
                  {(plan as any).badge === 'Best Value' && (
                    <Sparkles size={12} className="inline mr-1" />
                  )}
                  {(plan as any).badge}
                </span>
              </div>
            )}

            <div className="text-center mb-8">
              <h4 className="h4 mb-2">
                {plan.title.includes('D.R.I.V.E.') ? (
                  <>
                    <BrandWordmark /> {plan.title.replace('D.R.I.V.E.', '').trim()}
                  </>
                ) : (
                  plan.title
                )}
              </h4>
              <p className="text-muted-foreground text-sm mb-6">{plan.description}</p>
              <div className="mb-3">
                <span className="text-4xl xl:text-5xl font-bold">
                  {formatPrice(plan?.annual?.price || 0)}
                </span>
              </div>
              {/* Savings text removed for one-time pricing */}
            </div>

            <ul className="space-y-4 mb-8 flex-grow">
              {plan.features &&
                plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <Check className="mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-sm">
                      {feature.includes('D.R.I.V.E.') ? (
                        <>
                          {feature.split('D.R.I.V.E.')[0]}
                          <BrandWordmark />
                          {feature.split('D.R.I.V.E.')[1]}
                        </>
                      ) : (
                        feature
                      )}
                    </span>
                  </li>
                ))}
            </ul>

            <Button
              variant={'default'}
              size="xl"
              onClick={() => handleSubscribe(plan)}
              className="w-full flex items-center justify-center gap-2 group-hover:shadow-lg"
            >
              Buy Now
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        ))}
      </div>

      {showAdditionalInfo && (
        <div className="mt-20 text-center">
          <div className="max-w-6xl mx-auto">
            <h3 className="h2 mb-8">
              Why Choose <BrandWordmark />?
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="flex flex-col rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 overflow-hidden">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Zap className="text-primary" size={32} />
                  </div>
                  <h4 className="h5 mb-3">Proven Results</h4>
                  <p className="text-muted-foreground text-sm">
                    Thousands of entrepreneurs have transformed their businesses with our proven
                    methodology.
                  </p>
                </div>
                <Image
                  src="/images/Sales.png"
                  alt="Proven Results"
                  width={300}
                  height={200}
                  className="w-full h-auto mt-auto"
                />
              </div>
              <div className="flex flex-col rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 overflow-hidden">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Shield className="text-primary" size={32} />
                  </div>
                  <h4 className="h5 mb-3">See the World Differently</h4>
                  <p className="text-muted-foreground text-sm">
                    Understand people and their deepest motivations with just a few questions.
                  </p>
                </div>
                <Image
                  src="/images/See.png"
                  alt="See the World Differently"
                  width={300}
                  height={200}
                  className="w-full h-auto mt-auto"
                />
              </div>
              <div className="flex flex-col rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 overflow-hidden">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Sparkles className="text-primary" size={32} />
                  </div>
                  <h4 className="h5 mb-3">Brilliant Ideas</h4>
                  <p className="text-muted-foreground text-sm">
                    Use DRIVE AI to do cool things with your company that you never dreamed were
                    possible.
                  </p>
                </div>
                <Image
                  src="/images/Brilliant.png"
                  alt="Brilliant Ideas"
                  width={300}
                  height={200}
                  className="w-full h-auto mt-auto"
                />
              </div>
              <div className="flex flex-col rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 overflow-hidden">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Star className="text-primary" size={32} />
                  </div>
                  <h4 className="h5 mb-3">Risk-Free Guarantee</h4>
                  <p className="text-muted-foreground text-sm">
                    If you are dissatisfied with your product purchase within 30 days, we&apos;ll
                    give you your money back.
                  </p>
                </div>
                <Image
                  src="/images/Guarantee.jpeg"
                  alt="Risk-Free Guarantee"
                  width={300}
                  height={200}
                  className="w-full h-auto mt-auto"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <HostedCheckoutModal
        isOpen={isModalOpen}
        onClose={closeModal}
        selectedPlan={selectedPlan}
        billingCycle={billingCycle}
        serializedUser={serializedUser}
        productOverride={productOverride}
        isGift={isGift}
      />
    </div>
  )
}
